import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  UserPlus, Eye, EyeOff, AlertCircle, CheckCircle, Loader2,
  LogIn, Search, Shield, X, ArrowLeft, Camera
} from 'lucide-react';
import axios from 'axios';
import { prepareImageFile } from '../components/member/shared';
import './Register.css';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const TURNSTILE_SITE_KEY = '0x4AAAAAAD8DqRvauFgRbwdq'; // FHM widget (Freiheitsmacher) - Live Site Key

function Register() {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    sponsor_username: '',
    bio: '',
    birthday_day: '',
    birthday_month: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [sponsorStatus, setSponsorStatus] = useState(null);
  const [sponsorName, setSponsorName] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileError, setTurnstileError] = useState('');
  const [profileImage, setProfileImage] = useState(null);
  const [profileImageUrl, setProfileImageUrl] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const turnstileRef = useRef(null);
  const turnstileWidgetId = useRef(null);
  const sponsorTimeout = useRef(null);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  const { register } = useAuth();

  // Password strength
  const getPasswordStrength = (pwd) => {
    const checks = [
      { label: 'Min. 8 Zeichen', valid: pwd.length >= 8 },
      { label: 'Großbuchstabe', valid: /[A-Z]/.test(pwd) },
      { label: 'Kleinbuchstabe', valid: /[a-z]/.test(pwd) },
      { label: 'Zahl', valid: /[0-9]/.test(pwd) },
      { label: 'Sonderzeichen', valid: /[!@#$%^&*(),.?":{}|<>]/.test(pwd) },
    ];
    const score = checks.filter((c) => c.valid).length;
    return { checks, score };
  };

  const strength = getPasswordStrength(formData.password);
  const strengthPercent = (strength.score / 5) * 100;
  const strengthColor =
    strength.score <= 2 ? '#FF0055' : strength.score <= 3 ? '#FFD700' : '#2D8B4E';

  // Sponsor validation by username (debounced)
  const validateSponsor = useCallback(async (username) => {
    if (!username || username.length < 3) {
      setSponsorStatus(null);
      setSponsorName('');
      return;
    }
    setSponsorStatus('checking');
    try {
      const resp = await axios.get(
        `${API_URL}/api/community/validate-sponsor/${encodeURIComponent(username)}`
      );
      if (resp.data.valid) {
        setSponsorStatus('valid');
        setSponsorName(resp.data.sponsor_name);
      } else {
        setSponsorStatus('invalid');
        setSponsorName(resp.data.message || '');
      }
    } catch {
      setSponsorStatus('invalid');
      setSponsorName('');
    }
  }, []);

  useEffect(() => {
    if (sponsorTimeout.current) clearTimeout(sponsorTimeout.current);
    sponsorTimeout.current = setTimeout(() => {
      validateSponsor(formData.sponsor_username);
    }, 500);
    return () => clearTimeout(sponsorTimeout.current);
  }, [formData.sponsor_username, validateSponsor]);

  // Profile image upload
  const processImageFile = async (file) => {
    if (!file) return;
    let toUpload;
    try {
      toUpload = await prepareImageFile(file);
    } catch (err) {
      setError(err.message);
      return;
    }
    setUploadingImage(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', toUpload);
      const resp = await axios.post(`${API_URL}/api/community/register/upload-image`, fd);
      setProfileImageUrl(resp.data.image_url);
      setProfileImage(URL.createObjectURL(toUpload));
    } catch (err) {
      setError(err.response?.data?.detail || 'Bild-Upload fehlgeschlagen');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleImageUpload = async (e) => {
    const input = e.target;
    await processImageFile(e.target.files[0]);
    input.value = '';
  };

  // Load Turnstile
  useEffect(() => {
    const scriptId = 'cf-turnstile-script';
    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }

    const interval = setInterval(() => {
      if (window.turnstile && turnstileRef.current && turnstileWidgetId.current === null) {
        try {
          turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
            sitekey: TURNSTILE_SITE_KEY,
            theme: 'auto',
            action: 'registration',
            retry: 'auto',
            callback: (token) => {
              setTurnstileToken(token);
              setTurnstileError('');
            },
            'error-callback': (code) => {
              setTurnstileToken('');
              setTurnstileError(
                String(code).includes('110200')
                  ? 'Diese Domain ist nicht für die Sicherheitsprüfung freigegeben. Bitte kontaktieren Sie den Support.'
                  : 'Sicherheitsprüfung fehlgeschlagen. Bitte laden Sie die Seite neu und versuchen Sie es erneut.'
              );
              return true; // als behandelt markieren -> verhindert unbehandelten Script-Fehler
            },
            'expired-callback': () => setTurnstileToken(''),
            'timeout-callback': () => setTurnstileToken(''),
          });
        } catch (e) {
          // Render-Fehler abfangen, damit die Seite nicht abstürzt
        }
        clearInterval(interval);
      }
    }, 300);

    return () => {
      clearInterval(interval);
      if (turnstileWidgetId.current !== null && window.turnstile) {
        try { window.turnstile.remove(turnstileWidgetId.current); } catch (e) {}
        turnstileWidgetId.current = null;
      }
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Leerzeichen (z.B. durch iOS-Autokorrektur eingefügt) in diesen Feldern automatisch entfernen
    const clean = ['username', 'sponsor_username', 'email'].includes(name) ? value.replace(/\s+/g, '') : value;
    setFormData((prev) => ({ ...prev, [name]: clean }));
  };

  const getMissingItems = () => {
    const nameRegex = /^[a-zA-ZäöüÄÖÜß\s\-]+$/;
    const items = [];
    if (!formData.first_name.trim()) items.push('Vorname eingeben');
    else if (!nameRegex.test(formData.first_name.trim())) items.push('Vorname: nur Buchstaben, Leerzeichen und Bindestriche');
    if (!formData.last_name.trim()) items.push('Nachname eingeben');
    else if (!nameRegex.test(formData.last_name.trim())) items.push('Nachname: nur Buchstaben, Leerzeichen und Bindestriche');
    if (!formData.username) items.push('Benutzername eingeben');
    else if (formData.username.length < 3) items.push('Benutzername: mindestens 3 Zeichen');
    else if (!/^[a-zA-Z0-9_\-]+$/.test(formData.username)) items.push('Benutzername: nur Buchstaben, Zahlen, - und _');
    if (!formData.email) items.push('E-Mail eingeben');
    else if (!/^\S+@\S+\.\S+$/.test(formData.email)) items.push('E-Mail: gültige Adresse eingeben');
    if (!profileImageUrl) items.push('Profilbild hochladen');
    if (sponsorStatus !== 'valid') items.push('Gültigen Förderer-Benutzernamen angeben');
    if (strength.score < 5) items.push('Alle Passwort-Anforderungen erfüllen');
    if (!formData.confirmPassword || formData.password !== formData.confirmPassword) items.push('Passwort bestätigen (beide müssen übereinstimmen)');
    if (!turnstileToken) items.push('Sicherheitsprüfung (Bot-Schutz) abwarten bzw. abschließen');
    return items;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const missing = getMissingItems();
    if (missing.length > 0) {
      setError(`Bitte noch erledigen: ${missing.join(' · ')}`);
      return;
    }

    setLoading(true);
    const birthday = formData.birthday_month && formData.birthday_day
      ? `${formData.birthday_month.padStart(2, '0')}-${formData.birthday_day.padStart(2, '0')}`
      : '';
    const result = await register({
      first_name: formData.first_name.trim(),
      last_name: formData.last_name.trim(),
      username: formData.username.trim(),
      email: formData.email.trim(),
      password: formData.password,
      sponsor_username: formData.sponsor_username.trim(),
      turnstile_token: turnstileToken,
      profile_image: profileImageUrl,
      bio: formData.bio,
      birthday,
    });

    if (result.success) {
      setSuccess(true);
    } else {
      setError(result.error);
      // Reset turnstile
      if (window.turnstile && turnstileWidgetId.current !== null) {
        window.turnstile.reset(turnstileWidgetId.current);
        setTurnstileToken('');
      }
    }
    setLoading(false);
  };

  if (success) {
    return (
      <div className="login-page" data-testid="register-success-page">
        <div className="login-container">
          <div className="login-card success-card">
            <div className="success-icon-wrap">
              <CheckCircle size={48} />
            </div>
            <h1>Fast geschafft!</h1>
            <p className="success-text">
              Wir haben dir eine E-Mail geschickt. Bitte klicke auf den Bestätigungslink darin,
              um deine E-Mail-Adresse zu bestätigen (schau auch im Spam-Ordner nach).
            </p>
            <p className="success-text" style={{ fontSize: '0.85rem', opacity: 0.85 }}>
              Erst nach der Bestätigung wird dein Förderer benachrichtigt und prüft deine Registrierung.
              Du erhältst eine E-Mail, sobald dein Konto freigeschaltet wurde.
            </p>
            <Link to="/" className="btn-login" data-testid="back-to-home-button">
              <ArrowLeft size={18} />
              <span>Zurück zur Startseite</span>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page" data-testid="register-page">
      <div className="register-container">
        <div className="login-card">
          <div className="login-header">
            <div className="login-icon-wrap">
              <UserPlus className="login-icon" />
            </div>
            <h1>Registrieren</h1>
            <p>Erstelle dein Konto bei Die Freiheitsmacher</p>
          </div>

          <form onSubmit={handleSubmit} className="login-form" noValidate data-testid="register-form">

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="first_name">Vorname</label>
                <input
                  type="text"
                  id="first_name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  placeholder="Max"
                  required
                  disabled={loading}
                  data-testid="register-first-name"
                />
                {formData.first_name && !/^[a-zA-ZäöüÄÖÜß\s\-]*$/.test(formData.first_name) && (
                  <span className="field-error">Nur Buchstaben erlaubt</span>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="last_name">Nachname</label>
                <input
                  type="text"
                  id="last_name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  placeholder="Mustermann"
                  required
                  disabled={loading}
                  data-testid="register-last-name"
                />
                {formData.last_name && !/^[a-zA-ZäöüÄÖÜß\s\-]*$/.test(formData.last_name) && (
                  <span className="field-error">Nur Buchstaben erlaubt</span>
                )}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="username">Benutzername</label>
              <input
                type="text"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleChange}
                placeholder="DeinBenutzername"
                required
                disabled={loading}
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                data-testid="register-username"
              />
              {formData.username && !/^[a-zA-Z0-9_\-]*$/.test(formData.username) && (
                <span className="field-error">Nur Buchstaben, Zahlen, - und _ erlaubt</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="email">E-Mail</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="deine@email.com"
                required
                disabled={loading}
                data-testid="register-email"
              />
            </div>

            <div className="form-group">
              <label>Profilbild *</label>
              <div
                className="profile-upload-area"
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); processImageFile(e.dataTransfer?.files?.[0]); }}
                data-testid="register-profile-image"
              >
                {profileImage ? (
                  <img src={profileImage} alt="Profilbild" className="profile-upload-preview" />
                ) : (
                  <div className="profile-upload-placeholder">
                    {uploadingImage ? <Loader2 size={24} className="spin" /> : <Camera size={24} />}
                    <span>{uploadingImage ? 'Wird hochgeladen...' : 'Profilbild waehlen'}</span>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={handleImageUpload}
                  style={{ display: 'none' }}
                  disabled={loading || uploadingImage}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="sponsor_username">Förderer-Benutzername</label>
              <div className="sponsor-input-wrap">
                <input
                  type="text"
                  id="sponsor_username"
                  name="sponsor_username"
                  value={formData.sponsor_username}
                  onChange={handleChange}
                  placeholder="Benutzername deines Förderers"
                  required
                  disabled={loading}
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  data-testid="register-sponsor-username"
                  className={sponsorStatus === 'valid' ? 'input-valid' : sponsorStatus === 'invalid' ? 'input-invalid' : ''}
                />
                <div className="sponsor-status">
                  {sponsorStatus === 'checking' && <Loader2 size={16} className="spin" />}
                  {sponsorStatus === 'valid' && <CheckCircle size={16} className="status-valid" />}
                  {sponsorStatus === 'invalid' && <X size={16} className="status-invalid" />}
                </div>
              </div>
              {sponsorStatus === 'valid' && (
                <div className="sponsor-found" data-testid="sponsor-found">
                  <Shield size={14} />
                  <span>Förderer: {sponsorName}</span>
                </div>
              )}
              {sponsorStatus === 'invalid' && (
                <div className="sponsor-not-found" data-testid="sponsor-not-found">
                  Kein aktiver Förderer mit diesem Benutzernamen gefunden
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="bio">Über mich (optional)</label>
              <textarea
                id="bio"
                name="bio"
                value={formData.bio}
                onChange={handleChange}
                placeholder="Erzähl etwas über dich..."
                disabled={loading}
                data-testid="register-bio"
                rows={3}
                style={{ resize: 'vertical', minHeight: '70px' }}
              />
            </div>

            <div className="form-group">
              <label>Geburtstag (Tag &amp; Monat)</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <select name="birthday_day" value={formData.birthday_day} onChange={handleChange} disabled={loading} data-testid="register-birthday-day" style={{ flex: 1 }}>
                  <option value="">Tag</option>
                  {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                    <option key={d} value={String(d)}>{d}</option>
                  ))}
                </select>
                <select name="birthday_month" value={formData.birthday_month} onChange={handleChange} disabled={loading} data-testid="register-birthday-month" style={{ flex: 1 }}>
                  <option value="">Monat</option>
                  {['Januar','Februar','Maerz','April','Mai','Juni','Juli','August','September','Oktober','November','Dezember'].map((m, i) => (
                    <option key={i+1} value={String(i+1)}>{m}</option>
                  ))}
                </select>
              </div>
            </div>


            <div className="form-group">
              <label htmlFor="password">Passwort</label>
              <div className="password-input-wrap">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Sicheres Passwort"
                  required
                  disabled={loading}
                  data-testid="register-password"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  data-testid="register-password-toggle"
                  aria-label={showPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {formData.password && (
                <div className="password-strength" data-testid="password-strength">
                  <div className="strength-bar">
                    <div
                      className="strength-fill"
                      style={{ width: `${strengthPercent}%`, background: strengthColor }}
                    />
                  </div>
                  <div className="strength-checks">
                    {strength.checks.map((check, i) => (
                      <span
                        key={`month-${i}`}
                        className={`strength-check ${check.valid ? 'valid' : ''}`}
                      >
                        {check.valid ? <CheckCircle size={12} /> : <X size={12} />}
                        {check.label}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Passwort bestätigen</label>
              <input
                type={showPassword ? 'text' : 'password'}
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Passwort wiederholen"
                required
                disabled={loading}
                data-testid="register-confirm-password"
                autoComplete="new-password"
              />
              {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                <span className="field-error">Passwörter stimmen nicht überein</span>
              )}
            </div>

            <div className="turnstile-wrap" ref={turnstileRef} data-testid="turnstile-widget"></div>

            {turnstileError && (
              <div className="form-error" data-testid="turnstile-error">
                <AlertCircle size={18} />
                <span>{turnstileError}</span>
              </div>
            )}

            {error && (
              <div className="form-error" data-testid="register-error">
                <AlertCircle size={18} />
                <span>{error}</span>
              </div>
            )}

            {(() => {
              const missing = getMissingItems();
              const touched = formData.first_name || formData.last_name || formData.username || formData.email || formData.password || profileImageUrl;
              if (missing.length === 0 || !touched) return null;
              return (
                <div className="register-missing" data-testid="register-missing-list">
                  <strong><AlertCircle size={14} /> Damit du dich registrieren kannst, fehlt noch:</strong>
                  <ul>
                    {missing.map((m, i) => <li key={i}>{m}</li>)}
                  </ul>
                </div>
              );
            })()}

            <button
              type="submit"
              className="btn-login"
              disabled={loading || uploadingImage}
              data-testid="register-submit-button"
            >
              {loading ? (
                <><Loader2 size={18} className="spin" /> Registrieren...</>
              ) : (
                <><UserPlus size={18} /> Registrieren</>
              )}
            </button>
          </form>

          <div className="login-footer">
            <p>Bereits registriert?</p>
            <Link to="/login" className="register-link" data-testid="login-link">
              <LogIn size={16} />
              <span>Zur Anmeldung</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Register;
